import {
  Alert,
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { EvacZone, FormEvacZoneStatus, GeoEventEvacZone } from 'shared/types';
import { Trans, useTranslation } from 'react-i18next';
import { getPendingZoneGroups } from 'features/evacZones/utils';
import {
  generatePendingZoneNames,
  isRenameHoldGroup,
} from './PendingEvacZonesWarning.utils';
import { EvacZoneStyles } from '../../../../constants';

// Resets form evacuation zone statuses when a pending zone group is discarded
const resetZoneGroupStatuses = (
  formEvacZoneStatuses: FormEvacZoneStatus[],
  resetZonesByUids: Record<string, EvacZone>,
): FormEvacZoneStatus[] => {
  // Track which zones we've processed to avoid duplication
  const processedZoneUids = new Set<string>();

  const updatedStatuses: FormEvacZoneStatus[] = formEvacZoneStatuses
    // filter out statuses that are being reset to normal
    .filter((status) => {
      const zone = resetZonesByUids[status.evacZone.uidV2];
      if (!zone) {
        return true;
      }

      processedZoneUids.add(status.evacZone.uidV2);

      return Boolean(zone.status);
    })
    .map((evacZoneStatus) => {
      const zone = resetZonesByUids[evacZoneStatus.evacZone.uidV2];
      if (zone?.status) {
        // update the status to the original status
        return {
          ...evacZoneStatus,
          status: zone.status, // Reset to current system status
        } as FormEvacZoneStatus;
      }
      return evacZoneStatus;
    });

  // readd any new statuses that were removed from previous updates - e.g accepted, and then set to normal
  const resetNewStatuses: FormEvacZoneStatus[] = Object.values(resetZonesByUids)
    .filter((zone) => zone.status && !processedZoneUids.has(zone.uidV2))
    .map(
      (zone) =>
        ({
          evacZone: {
            ...zone,
            regionEvacZoneStyle:
              zone.region.evacZoneStyle || EvacZoneStyles.default,
          } as Omit<GeoEventEvacZone, 'regionId'>,
          status: zone.status!, // Reset to current system status
        }) as FormEvacZoneStatus,
    );

  return [...updatedStatuses, ...resetNewStatuses];
};

type PendingEvacZonesWarningProps = {
  evacZones: EvacZone[];
};

type FormValues = {
  evacZoneStatuses: FormEvacZoneStatus[];
  releasePendingZoneUids?: string[];
};

export const PendingEvacZonesWarning = (
  props: PendingEvacZonesWarningProps,
): JSX.Element | null => {
  const { evacZones } = props;
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<FormValues>();

  const releasePendingZoneUids = watch('releasePendingZoneUids', [])!;
  const formEvacZoneStatuses = watch('evacZoneStatuses', [])!;

  const pendingZoneGroups = getPendingZoneGroups(evacZones);

  const hasPendingZoneGroups = Boolean(pendingZoneGroups.length);

  const handleChange = (
    action: 'add' | 'remove',
    pendingZoneUids: string[],
  ): void => {
    if (action === 'add') {
      setValue('releasePendingZoneUids', [
        ...releasePendingZoneUids,
        ...pendingZoneUids,
      ]);
      return;
    }

    setValue(
      'releasePendingZoneUids',
      releasePendingZoneUids.filter((uid) => !pendingZoneUids.includes(uid)),
    );

    // Find the group for the zones being cleared, a group matches if there's any overlap between the UIDs
    const targetGroups = pendingZoneGroups.filter((group) => {
      return group.releaseUids.some((uid) => pendingZoneUids.includes(uid));
    });

    if (targetGroups?.length > 0) {
      const resetZonesByUids = targetGroups
        .map((group) => group.zones)
        .reduce(
          (acc, zones) => {
            zones.forEach((zone) => {
              acc[zone.uidV2] = zone;
            });
            return acc;
          },
          {} as Record<string, EvacZone>,
        );

      const updatedStatuses = resetZoneGroupStatuses(
        formEvacZoneStatuses,
        resetZonesByUids,
      );

      setValue('evacZoneStatuses', updatedStatuses);
    }
  };

  const renameFlags = pendingZoneGroups.map(isRenameHoldGroup);
  const allGroupsAreRenames = renameFlags.every(Boolean);

  const releasePendingZoneUidSet = new Set(releasePendingZoneUids);

  if (!hasPendingZoneGroups) {
    return null;
  }

  return (
    <Grid size={12}>
      <Alert severity="warning" data-testid="pending-evac-zones-warning">
        <Stack spacing={2}>
          <Typography>
            {allGroupsAreRenames
              ? t('pendingEvacZonesWarning.renameTitle')
              : t('pendingEvacZonesWarning.title')}
          </Typography>

          {pendingZoneGroups.map((zoneGroup, index) => {
            const key = zoneGroup.releaseUids.join(',');
            const { activeNames, resultingNames } = generatePendingZoneNames(
              t,
              zoneGroup,
            );
            const isRename = renameFlags[index];
            const checked = zoneGroup.releaseUids.every((uid) =>
              releasePendingZoneUidSet.has(uid),
            );
            return (
              <Stack key={key} spacing={1} gap={1}>
                <Typography>
                  <Trans
                    i18nKey={
                      isRename
                        ? 'pendingEvacZonesWarning.renameMessage'
                        : 'pendingEvacZonesWarning.message'
                    }
                    values={{ activeNames, resultingNames }}
                  >
                    <Typography component="span" fontWeight="fontWeightBold">
                      {activeNames}
                    </Typography>
                    will be replaced by
                    <Typography component="span" fontWeight="fontWeightBold">
                      {resultingNames}
                    </Typography>
                  </Trans>
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      name="acceptPendingReview"
                      checked={checked}
                      onChange={() =>
                        handleChange(
                          checked ? 'remove' : 'add',
                          zoneGroup.releaseUids,
                        )
                      }
                      id="acceptPendingReview"
                    />
                  }
                  label={t('pendingEvacZonesWarning.accept')}
                  sx={{
                    '.MuiFormControlLabel-label': {
                      fontWeight: 'fontWeightBold',
                    },
                  }}
                />
              </Stack>
            );
          })}
        </Stack>
      </Alert>
    </Grid>
  );
};
